/**
 * `next/image` compatibility shim.
 *
 * Maps the `<Image>` API onto a native `<img>`. Next's optimization pipeline
 * (loader, blurDataURL placeholders, sizes hints, fetchPriority) is dropped —
 * the bundle is served from Jest's CDN as a static SPA, so there is no Image
 * Optimization API to call. width/height/alt/className pass through.
 */
import type { CSSProperties, ImgHTMLAttributes } from 'react';

interface NextImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'placeholder'> {
  src: string | { src: string; width?: number; height?: number };
  alt: string;
  width?: number | `${number}`;
  height?: number | `${number}`;
  fill?: boolean;
  priority?: boolean;
  loading?: 'lazy' | 'eager';
  quality?: number | `${number}`;
  placeholder?: 'blur' | 'empty' | `data:image/${string}`;
  blurDataURL?: string;
  unoptimized?: boolean;
  sizes?: string;
  style?: CSSProperties;
}

const FILL_STYLE: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

export default function Image({
  src,
  alt,
  width,
  height,
  fill,
  priority,
  loading,
  quality: _quality,
  placeholder: _placeholder,
  blurDataURL: _blurDataURL,
  unoptimized: _unoptimized,
  sizes: _sizes,
  style,
  ...rest
}: NextImageProps) {
  const resolvedSrc = typeof src === 'string' ? src : src.src;
  const resolvedWidth = width ?? (typeof src === 'object' ? src.width : undefined);
  const resolvedHeight = height ?? (typeof src === 'object' ? src.height : undefined);

  if (fill) {
    return (
      <img
        src={resolvedSrc}
        alt={alt}
        loading={priority ? 'eager' : loading ?? 'lazy'}
        style={{ ...FILL_STYLE, ...style }}
        {...rest}
      />
    );
  }

  return (
    <img
      src={resolvedSrc}
      alt={alt}
      width={resolvedWidth}
      height={resolvedHeight}
      loading={priority ? 'eager' : loading ?? 'lazy'}
      style={style}
      {...rest}
    />
  );
}
